import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Minus, Plus, RotateCcw } from "lucide-react";
import { api, API } from "../lib/api";
import { COUNTY_SHAPES, MAP_VIEWBOX } from "../data/kenyaCounties";

// One hue, four intensities. Colour only ever means activity; the corridor outline means priority.
const STATUS = {
  "Facility in progress": { fill: "#FF2E1F", label: "Facility in progress" },
  "Proposal received": { fill: "#C11409", label: "Proposal received" },
  Planned: { fill: "#7A0C05", label: "Planned" },
  "No activity yet": { fill: "#2B0705", label: "Open to proposals" },
};

// The worst-served scale: how far a county still is from Brazil's own density once everything proposed is built.
const GAP_BANDS = [
  { min: 600, fill: "#FF2E1F", label: "600+ facilities short" },
  { min: 300, fill: "#C11409", label: "300 to 599 short" },
  { min: 150, fill: "#7A0C05", label: "150 to 299 short" },
  { min: 0, fill: "#2B0705", label: "Under 150 short" },
];
const band = (n) => GAP_BANDS.find((b) => (n || 0) >= b.min) || GAP_BANDS[GAP_BANDS.length - 1];

const key = (n) => (n || "").trim().toLowerCase();
const num = (v) => (v || v === 0 ? Number(v).toLocaleString() : "—");
const money = (v) =>
  v >= 1e6 ? `$${(v / 1e6).toFixed(1)}m` : v || v === 0 ? `$${Math.round(v).toLocaleString()}` : "—";
const ZOOM_MIN = 1;
const ZOOM_MAX = 5;

const Stat = ({ label, value, note, testId }) => (
  <div className="border-t border-white/10 pt-3" data-testid={testId}>
    <p className="text-[0.6rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
    <p className="mono mt-1 text-base text-white">{value}</p>
    {note ? <p className="mt-1 text-[0.62rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p> : null}
  </div>
);

export const CountyMap = ({ counties = [], censusSource, censusSourceUrl, pop, pins = {} }) => {
  const byName = useMemo(() => {
    const m = {};
    counties.forEach((c) => {
      m[key(c.name)] = c;
    });
    return m;
  }, [counties]);

  const [active, setActive] = useState("Nairobi");
  const [hovered, setHovered] = useState(null);
  const [proOnly, setProOnly] = useState(false);
  const [colour, setColour] = useState("activity");
  const [gaps, setGaps] = useState(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [tip, setTip] = useState(null);
  const detailRef = useRef(null);
  const frameRef = useRef(null);
  const dragRef = useRef({ down: false, moved: 0, x: 0, y: 0, ox: 0, oy: 0 });

  const shown = hovered || active;
  const selected = shown ? byName[key(shown)] : null;
  const census = selected?.census || {};

  const pick = (name) => {
    if (dragRef.current.moved > 5) return;
    setHovered(null);
    setActive(name);
    if (window.matchMedia("(max-width: 1023px)").matches) {
      requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };

  const counts = useMemo(() => {
    const c = {};
    counties.forEach((x) => {
      c[x.status] = (c[x.status] || 0) + 1;
    });
    return c;
  }, [counties]);
  const proCount = counties.filter((c) => c.priority).length;
  void proCount;

  // The central corridor: the Priority 18 joined north-west to south-east, the way the road runs.
  const corridor = useMemo(() => {
    const pts = COUNTY_SHAPES.filter((s) => byName[key(s.name)]?.priority).map((s) => ({
      name: s.name,
      cx: s.cx,
      cy: s.cy,
    }));
    pts.sort((a, b) => a.cx + a.cy - (b.cx + b.cy));
    return pts;
  }, [byName]);

  // The gap data is only fetched when the reader asks for it: it recomputes the whole allocation model.
  useEffect(() => {
    if (colour !== "gap" || gaps) return;
    api.get("/national-rollup")
      .then((r) => {
        const m = {};
        r.data.rows.forEach((row) => { m[key(row.county)] = row; });
        setGaps(m);
      })
      .catch(() => setGaps("error"));
  }, [colour, gaps]);

  const zoom = (delta) =>
    setView((v) => {
      const scale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(v.scale + delta).toFixed(2)));
      return scale === ZOOM_MIN ? { scale, x: 0, y: 0 } : { ...v, scale };
    });

  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 12) return;
    e.preventDefault();
    zoom(e.deltaY > 0 ? -0.25 : 0.25);
  };

  const onPointerDown = (e) => {
    dragRef.current = {
      down: true,
      moved: 0,
      x: e.clientX,
      y: e.clientY,
      ox: view.x,
      oy: view.y,
    };
  };
  const onPointerMove = (e) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (rect) setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    const d = dragRef.current;
    if (!d.down) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    d.moved = Math.max(d.moved, Math.abs(dx) + Math.abs(dy));
    if (d.moved > 5 && view.scale > 1) setView((v) => ({ ...v, x: d.ox + dx, y: d.oy + dy }));
  };
  const onPointerUp = () => {
    dragRef.current.down = false;
    window.setTimeout(() => {
      dragRef.current.moved = 0;
    }, 60);
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_390px]" data-testid="county-map">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setProOnly(false)}
            data-testid="county-view-all"
            className={`border px-4 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
              !proOnly ? "border-ochre text-ochre" : "border-white/10 text-zinc-500 hover:text-white"
            }`}
          >
            All 47 counties
          </button>
          <button
            onClick={() => setProOnly(true)}
            data-testid="county-view-pro"
            className={`border px-4 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
              proOnly ? "border-ochre text-ochre" : "border-white/10 text-zinc-500 hover:text-white"
            }`}
          >
            The Priority 18
          </button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="county-picker-row">
          <label htmlFor="county-picker" className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400">
            Jump to a county
          </label>
          <select
            id="county-picker"
            value={active}
            onChange={(e) => pick(e.target.value)}
            data-testid="county-picker"
            className="border border-white/20 bg-black px-3 py-2 text-sm text-white outline-none focus:border-ochre"
          >
            {[...counties].sort((x, y) => x.name.localeCompare(y.name)).map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-1" data-testid="county-map-zoom">
            <button
              onClick={() => zoom(0.5)}
              aria-label="Zoom in"
              data-testid="county-zoom-in"
              className="border border-white/15 p-2 text-zinc-400 transition-colors hover:border-maasai hover:text-white"
            >
              <Plus size={13} />
            </button>
            <button
              onClick={() => zoom(-0.5)}
              aria-label="Zoom out"
              data-testid="county-zoom-out"
              className="border border-white/15 p-2 text-zinc-400 transition-colors hover:border-maasai hover:text-white"
            >
              <Minus size={13} />
            </button>
            <button
              onClick={() => setView({ scale: 1, x: 0, y: 0 })}
              aria-label="Reset the map view"
              data-testid="county-zoom-reset"
              className="border border-white/15 p-2 text-zinc-400 transition-colors hover:border-maasai hover:text-white"
            >
              <RotateCcw size={13} />
            </button>
            <span className="mono ml-2 text-[0.58rem] uppercase tracking-[0.14em] text-zinc-400">
              {view.scale.toFixed(1)}x
            </span>
          </div>
        </div>

        <div
          ref={frameRef}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => {
            onPointerUp();
            setTip(null);
            setHovered(null);
          }}
          className={`relative mt-6 select-none overflow-hidden border border-maasai/25 bg-[#050505] p-3 ${
            view.scale > 1 ? "cursor-grab active:cursor-grabbing" : ""
          }`}
          data-testid="county-map-frame"
        >
          <div className="pointer-events-none absolute inset-3 opacity-[0.1] shuka-check" aria-hidden="true" />
          <div
            className="pointer-events-none absolute inset-0"
            aria-hidden="true"
            style={{ background: "radial-gradient(circle at 50% 42%, rgba(227,33,24,0.16), transparent 62%)" }}
          />
          <svg
            viewBox={MAP_VIEWBOX}
            className="relative h-auto w-full max-h-[740px]"
            role="img"
            aria-label="Interactive Kenya county map"
          >
            <defs>
              <pattern id="ke-grid" width="26" height="26" patternUnits="userSpaceOnUse">
                <path d="M26 0H0V26" fill="none" stroke="#1a0605" strokeWidth="1" />
              </pattern>
              <filter id="ke-glow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="9" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <linearGradient id="ke-corridor" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF6B5A" stopOpacity="0.1" />
                <stop offset="50%" stopColor="#FF2E1F" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#FF6B5A" stopOpacity="0.1" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="1000" height="1150" fill="url(#ke-grid)" />

            <g
              transform={`translate(${view.x} ${view.y}) scale(${view.scale})`}
              style={{ transformOrigin: "500px 575px", transition: dragRef.current.down ? "none" : "transform 240ms ease-out" }}
            >
              {COUNTY_SHAPES.map((shape) => {
                const county = byName[key(shape.name)];
                const status = STATUS[county?.status] || STATUS["No activity yet"];
                const gapRow = colour === "gap" && gaps && gaps !== "error" ? gaps[key(shape.name)] : null;
                const fill = colour === "gap" ? band(gapRow?.brazil_shortfall).fill : status.fill;
                const isPro = !!county?.priority;
                const dim = proOnly && !isPro;
                const isActive = key(shape.name) === key(shown);
                return (
                  <path
                    key={shape.code}
                    d={shape.d}
                    fill={dim ? "#160303" : fill}
                    stroke={isActive ? "#ffffff" : isPro ? "#FF8C7A" : "#1c0504"}
                    strokeWidth={isActive ? 3.5 : isPro ? 1.8 : 0.9}
                    strokeOpacity={isActive ? 1 : isPro ? 0.75 : 0.9}
                    opacity={dim ? 0.4 : 0.96}
                    filter={isActive ? "url(#ke-glow)" : undefined}
                    tabIndex={0}
                    role="button"
                    aria-label={colour === "gap"
                      ? `${shape.name} county — ${gapRow?.brazil_shortfall ?? 0} facilities short of Brazil`
                      : `${shape.name} county — ${county?.status || "Open to proposals"}`}
                    className={`cursor-pointer outline-none transition-all duration-200 hover:brightness-[1.45] ${
                      isActive ? "animate-pulse" : ""
                    }`}
                    data-testid={`county-shape-${key(shape.name).replace(/[^a-z0-9]+/g, "-")}`}
                    onMouseEnter={() => setHovered(shape.name)}
                    onFocus={() => setHovered(shape.name)}
                    onClick={() => pick(shape.name)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        pick(shape.name);
                      }
                    }}
                  />
                );
              })}

              {corridor.length > 1 ? (
                <g className="pointer-events-none" data-testid="county-corridor">
                  <polyline
                    points={corridor.map((p) => `${p.cx},${p.cy}`).join(" ")}
                    fill="none"
                    stroke="url(#ke-corridor)"
                    strokeWidth={proOnly ? 4 : 2.2}
                    strokeDasharray="10 8"
                    opacity={proOnly ? 0.95 : 0.4}
                  />
                  {corridor.map((p) => (
                    <circle key={p.name} cx={p.cx} cy={p.cy} r={proOnly ? 5 : 3.4} fill="#FF8C7A" opacity="0.9" />
                  ))}
                </g>
              ) : null}

              {COUNTY_SHAPES.filter((s) => (pins[s.name] || 0) > 0).map((s) => {
                const n = pins[s.name];
                const r = Math.min(9 + n * 2.5, 22);
                return (
                  <g key={`pin-${s.code}`} className="pointer-events-none" data-testid={`county-pin-${key(s.name).replace(/[^a-z0-9]+/g, "-")}`}>
                    <circle cx={s.cx} cy={s.cy} r={r + 6} fill="#E5A91E" opacity="0.18" />
                    <circle cx={s.cx} cy={s.cy} r={r} fill="#E5A91E" stroke="#000" strokeWidth="1.5" />
                    <text x={s.cx} y={s.cy + 5} textAnchor="middle" fontFamily="Oswald, sans-serif"
                      fontSize={r > 14 ? 16 : 13} fill="#0b0b0b" fontWeight="700">
                      {n}
                    </text>
                  </g>
                );
              })}

              {COUNTY_SHAPES.filter((s) => key(s.name) === key(shown)).map((s) => (
                <text
                  key={`t-${s.code}`}
                  x={s.cx}
                  y={s.cy}
                  textAnchor="middle"
                  className="pointer-events-none select-none"
                  fontFamily="Oswald, sans-serif"
                  fontSize="27"
                  fill="#ffffff"
                  stroke="#000000"
                  strokeWidth="6"
                  paintOrder="stroke"
                >
                  {s.name.toUpperCase()}
                </text>
              ))}
            </g>
          </svg>

          {hovered && tip ? (
            <div
              className="pointer-events-none absolute z-10 w-[210px] border border-maasai/50 bg-black/90 p-3 backdrop-blur"
              style={{
                left: Math.min(Math.max(tip.x + 14, 8), (frameRef.current?.clientWidth || 400) - 220),
                top: Math.max(tip.y - 10, 8),
              }}
              data-testid="county-map-tooltip"
            >
              <p className="font-display text-sm uppercase tracking-tight text-white">{hovered}</p>
              <p className="mono mt-1 text-[0.62rem] uppercase tracking-[0.14em] text-ochre">
                {byName[key(hovered)]?.status || "Open to proposals"}
              </p>
              <p className="mono mt-2 text-[0.62rem] text-zinc-400">
                {num(byName[key(hovered)]?.census?.population)} people
              </p>
              {colour === "gap" && gaps && gaps !== "error" ? (
                <p className="mono mt-1 text-[0.6rem] uppercase tracking-[0.14em] text-maasai"
                  data-testid="county-map-tooltip-gap">
                  {num(gaps?.[key(hovered)]?.brazil_shortfall)} short of Brazil ·{" "}
                  {gaps?.[key(hovered)]?.brazil_share_pct ?? 0}% of its density
                </p>
              ) : null}
              {pins[hovered] ? (
                <p className="mono mt-1 text-[0.6rem] uppercase tracking-[0.14em] text-ochre">
                  {pins[hovered]} site{pins[hovered] === 1 ? "" : "s"} filed here
                </p>
              ) : null}
              {byName[key(hovered)]?.priority ? (
                <p className="mono mt-1 text-[0.58rem] uppercase tracking-[0.14em] text-maasai">
                  Priority 18 · #{byName[key(hovered)]?.priority_rank}
                </p>
              ) : null}
            </div>
          ) : null}

          <p className="mt-2 text-center text-[0.55rem] uppercase tracking-[0.2em] text-zinc-400">
            Hover or tap a county · click to pin it · scroll or use the buttons to zoom, drag to pan
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-3" data-testid="county-map-colour-modes">
          {[["activity", "Colour by activity"], ["gap", "Colour by the Brazil gap"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setColour(id)} data-testid={`county-map-colour-${id}`}
              className={`mono border px-4 py-2 text-[0.58rem] uppercase tracking-[0.16em] transition-colors ${
                colour === id ? "border-ochre text-ochre" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
          {colour === "gap" ? (
            <Link to="/counties/rollup" className="mono border border-zinc-700 px-4 py-2 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400 transition-colors hover:border-ochre hover:text-ochre"
              data-testid="county-map-to-rollup">
              Worst served, ranked
            </Link>
          ) : null}
        </div>

        {colour === "gap" ? (
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3" data-testid="county-map-gap-legend">
            {gaps === "error" ? (
              <span className="text-[0.62rem] uppercase tracking-[0.16em] text-maasai" data-testid="county-map-gap-error">
                The gap figures did not load. Refresh to try again.
              </span>
            ) : !gaps ? (
              <span className="text-[0.62rem] uppercase tracking-[0.16em] text-zinc-400" data-testid="county-map-gap-loading">
                Totalling the gap in all 47 counties…
              </span>
            ) : null}
            {GAP_BANDS.map((b) => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="h-3 w-6" style={{ background: b.fill }} />
                <span className="text-[0.62rem] uppercase tracking-[0.16em] text-zinc-400">{b.label}</span>
              </div>
            ))}
            <span className="text-[0.62rem] uppercase tracking-[0.16em] text-zinc-500">
              Facilities still short of Brazil&rsquo;s published density per million, after everything proposed here is built
            </span>
          </div>
        ) : null}

        <div className={`mt-6 flex flex-wrap gap-x-8 gap-y-3 ${colour === "gap" ? "opacity-45" : ""}`} data-testid="county-map-legend">
          {Object.entries(STATUS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <span className="h-3 w-6" style={{ background: v.fill }} />
              <span className="text-[0.62rem] uppercase tracking-[0.16em] text-zinc-400">
                {v.label} <span className="mono text-zinc-400">{counts[k] || 0}</span>
              </span>
            </div>
          ))}
          <div className="flex items-center gap-3" data-testid="county-legend-pins">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-ochre text-[0.5rem] font-bold text-black">3</span>
            <span className="text-[0.62rem] uppercase tracking-[0.16em] text-zinc-400">
              Sites filed by the public
            </span>
          </div>
          <div className="flex items-center gap-3" data-testid="county-legend-priority">
            <span className="h-3 w-6 border" style={{ borderColor: "#FF8C7A" }} />
            <span className="text-[0.62rem] uppercase tracking-[0.16em] text-zinc-400">
              Priority 18, on the central corridor
            </span>
          </div>
        </div>
      </div>

      <div ref={detailRef} className="border border-zinc-800 bg-[#0b0b0b] p-6 scroll-mt-24" data-testid="county-detail">
        {selected ? (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="overline text-ochre">{selected.status}</p>
                <h3 className="mt-3 font-display text-2xl uppercase tracking-tight text-white">
                  {selected.name} County
                </h3>
              </div>
              {selected.priority ? (
                <span
                  className="shrink-0 border border-ochre px-2 py-1 text-[0.52rem] font-bold uppercase tracking-[0.14em] text-ochre"
                  data-testid="county-pro-tag"
                >
                  Priority 18 · #{selected.priority_rank}
                </span>
              ) : null}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4" data-testid="county-census">
              <Stat label="Population (2019 census)" value={num(census.population)} testId="county-population" />
              <Stat
                label="Population today (projected)"
                value={num(selected.projected?.population)}
                note={pop?.scale_factor ? `2019 census x ${pop.scale_factor} (Worldometer)` : ""}
                testId="county-population-now"
              />
              <Stat
                label="Women per man"
                value={census.women_per_man ? census.women_per_man.toFixed(3) : "—"}
                note={census.female ? `${num(census.female)} women · ${num(census.male)} men` : ""}
                testId="county-ratio"
              />
              <Stat label="Households" value={num(census.households)} testId="county-households" />
              <Stat
                label="Density"
                value={census.density ? `${num(Math.round(census.density))}/km²` : "—"}
                note={census.area_sqkm ? `${num(Math.round(census.area_sqkm))} km²` : ""}
                testId="county-density"
              />
              <Stat
                label="GDP (PPP) estimate"
                value={selected.gdp_ppp_usd_bn ? `$${selected.gdp_ppp_usd_bn}bn` : "Researching"}
                note={selected.gdp_ppp_note}
                testId="county-gdp"
              />
              <Stat
                label="Youth (15–24)"
                value={selected.youth_population ? num(selected.youth_population) : "Researching"}
                testId="county-youth"
              />
              <Stat
                label="In secondary school"
                value={selected.secondary_enrolment ? num(selected.secondary_enrolment) : "Researching"}
                testId="county-secondary"
              />
              <Stat
                label="In primary school"
                value={selected.primary_enrolment ? num(selected.primary_enrolment) : "Researching"}
                testId="county-primary"
              />
            </div>

            {selected.impact ? (
              <div className="mt-7 border border-white/10 bg-[#0b0b0b] p-5" data-testid="county-impact-card">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="overline text-ochre">This county&rsquo;s share</p>
                  <p className="mono text-xs text-zinc-400" data-testid="county-impact-share">
                    {selected.impact.population_share_pct}% of Kenyans
                  </p>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4">
                  <Stat label="Jobs" value={num(selected.impact.jobs)}
                        note={`+${num(selected.impact.construction_job_years)} build person-years`}
                        testId="county-impact-jobs" />
                  <Stat label="Teams" value={num(selected.impact.teams)}
                        note={`${num(selected.impact.matches_per_year)} matches a year`}
                        testId="county-impact-teams" />
                  <Stat label="Health savings" value={money(selected.impact.health_savings_usd)}
                        note="Per year, at Kenyan health costs" testId="county-impact-health" />
                  <Stat label="Coaching places" value={num(selected.impact.coaching_places)}
                        note={`${num(selected.impact.referee_places)} referee places`}
                        testId="county-impact-coaching" />
                  <Stat label="Players served" value={num(selected.impact.players)} testId="county-impact-players" />
                  <Stat label="Total annual value" value={money(selected.impact.total_value_usd)}
                        note="Economic, health and social" testId="county-impact-value" />
                </div>
                <p className="mt-5 text-xs leading-relaxed text-zinc-400">{selected.impact.basis}</p>
              </div>
            ) : null}

            {selected.projects?.length ? (
              <div className="mt-7" data-testid="county-projects">
                <p className="overline">Infrastructure under way</p>
                <ul className="mt-4 space-y-3">
                  {selected.projects.map((p) => (
                    <li key={p} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-ochre" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className="mt-7 text-sm leading-relaxed text-zinc-400">{selected.note}</p>
            {selected.programmes ? (
              <p className="mt-4 border-l-2 border-maasai pl-4 text-sm text-zinc-400">{selected.programmes}</p>
            ) : null}
            {selected.proposal_count ? (
              <p className="mono mt-4 text-xs text-ochre" data-testid="county-proposal-count">
                {selected.proposal_count} proposal{selected.proposal_count === 1 ? "" : "s"} submitted here
              </p>
            ) : null}

            <p className="mt-6 text-[0.62rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
              Census figures: {censusSource || "KNBS 2019 census"}
              {censusSourceUrl ? (
                <>
                  {" · "}
                  <a href={censusSourceUrl} target="_blank" rel="noreferrer" className="underline hover:text-ochre">
                    source
                  </a>
                </>
              ) : null}
              {selected.data_status ? ` · GDP, enrolment and projects: ${selected.data_status}` : ""}
              {pop?.worldometer_url ? (
                <>
                  {" · Projected to today from "}
                  <a href={pop.worldometer_url} target="_blank" rel="noreferrer" className="underline hover:text-ochre" data-testid="county-worldometer-link">
                    Worldometer
                  </a>
                </>
              ) : null}
            </p>

            <Link to={`/counties/${selected.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="btn-primary mt-7" data-testid="county-dossier-link">
              Full county dossier
            </Link>
            <Link to={`/counties/${selected.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}/one-page`} className="btn-ghost mt-3" data-testid="county-one-pager-link">
              {selected.name} on one page
            </Link>
            <Link to={`/plans?county=${encodeURIComponent(selected.name)}&track=professional`} className="btn-ghost mt-3" data-testid="county-steps-link">
              Recommended steps for {selected.name}
            </Link>
            <Link to="/compare" className="btn-ghost mt-3" data-testid="county-compare-link">
              Compare with another county
            </Link>
            <Link to={`/apply`} className="btn-ghost mt-3" data-testid="county-proposal-cta">
              Propose something here
            </Link>
            <Link to={`/apply?county=${encodeURIComponent(selected.name)}`} className="btn-ghost mt-3" data-testid="county-apply-cta">
              File a paid proposal for {selected.name}
            </Link>
            <a
              href={`${API}/steps-pack.pdf?track=professional&county=${encodeURIComponent(selected.name)}`}
              className="mt-3 flex items-center gap-2 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-ochre underline underline-offset-4 hover:text-white"
              data-testid="county-steps-pack"
            >
              <Download size={13} /> {selected.name}&rsquo;s recommended steps pack (PDF)
            </a>
            <a
              href={`${API}/county-prospectus/${encodeURIComponent(selected.name)}.pdf`}
              className="mt-3 flex items-center gap-2 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-ochre underline underline-offset-4 hover:text-white"
              data-testid="county-business-case"
            >
              <Download size={13} /> Download {selected.name}&rsquo;s business case (PDF)
            </a>
          </>
        ) : (
          <>
            <p className="overline">Pick a county</p>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Hover or tap any county for its 2019 census population, women-to-men ratio, schooling, GDP estimate and
              live infrastructure projects.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
